import {
  IAddProductInput,
  IAddProductVariantInput,
  ICategory,
  IProduct,
  priceFormat,
} from "muminsmart-utils";
import React, { FunctionComponent } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import styles from "./ProductVariantSummary.module.css";
import { Button } from "@/components/ui/button";
import { useCategoryVariantAttributes } from "@/app/components/product/utils";

const VIRTUALIZE_THRESHOLD = 20;
const ESTIMATED_VARIANT_SUMMARY_HEIGHT = 400;
const VARIANT_SUMMARY_GAP = 20;

interface ProductVariantSummaryProps {
  categories: ICategory[];
  selectedProduct: IProduct | null;
  onSeePreview: () => void;
}

interface VariantSummaryCardProps {
  variant: IAddProductVariantInput;
  index: number;
  attributeNameById: Map<string, string>;
}

const VariantSummaryCard: React.FC<VariantSummaryCardProps> = ({
  variant,
  index,
  attributeNameById,
}) => {
  return (
    <div className="mb-5">
      <h2 className="text-md font-semibold text-black border-b border-black pb-2 mb-4">
        Variant {index + 1}
      </h2>

      <div className={styles.variantDetails}>
        {variant.variantAttributes.map((attr, ai) => (
          <div
            key={`${attr.attributeId}-${ai}`}
            className="px-4 py-3 flex justify-between text-gray-700"
          >
            <span>
              {attributeNameById.get(attr.attributeId) ?? attr.attributeId}
            </span>
            <span className="font-medium">{attr.value}</span>
          </div>
        ))}
        <div className="px-4 py-3 flex justify-between text-gray-700">
          <span>Weight (kgs)</span>
          <span className="font-medium">{variant.weight} kg</span>
        </div>
        <div className="px-4 py-3 flex justify-between text-gray-700">
          <span>Price</span>
          <span className="font-medium">{priceFormat(variant.price)}</span>
        </div>
        <div className="px-4 py-3 flex justify-between text-gray-700">
          <span>Discounted Price</span>
          <span className="font-medium">
            {variant.discountedPrice
              ? priceFormat(variant.discountedPrice)
              : "N/A"}
          </span>
        </div>
        <div className="px-4 py-3 flex justify-between text-gray-700">
          <span>SKU</span>
          <span className="font-medium">{variant.sku}</span>
        </div>
        <div className="px-4 py-3 flex justify-between text-gray-700">
          <span>Stock</span>
          <span className="font-medium">
            {variant.alwaysAvailable ? "Always available" : variant.stock}
          </span>
        </div>
      </div>
    </div>
  );
};

interface VirtualizedVariantSummaryRowsProps {
  variants: IAddProductVariantInput[];
  attributeNameById: Map<string, string>;
}

const VirtualizedVariantSummaryRows: React.FC<
  VirtualizedVariantSummaryRowsProps
> = ({ variants, attributeNameById }) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = React.useState(0);

  React.useLayoutEffect(() => {
    const updateScrollMargin = () => {
      setScrollMargin(listRef.current?.offsetTop ?? 0);
    };

    updateScrollMargin();
    window.addEventListener("resize", updateScrollMargin);

    return () => window.removeEventListener("resize", updateScrollMargin);
  }, [variants.length]);

  const virtualizer = useWindowVirtualizer({
    count: variants.length,
    estimateSize: () => ESTIMATED_VARIANT_SUMMARY_HEIGHT,
    overscan: 3,
    gap: VARIANT_SUMMARY_GAP,
    scrollMargin,
  });

  return (
    <div ref={listRef} className="w-full">
      <div
        className="relative w-full"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const variant = variants[virtualItem.index];

          if (!variant) {
            return null;
          }

          return (
            <div
              key={`${variant.sku}-${virtualItem.index}`}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              className="absolute top-0 left-0 w-full"
              style={{
                transform: `translateY(${virtualItem.start - scrollMargin}px)`,
              }}
            >
              <VariantSummaryCard
                variant={variant}
                index={virtualItem.index}
                attributeNameById={attributeNameById}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ProductVariantSummary: FunctionComponent<ProductVariantSummaryProps> = ({
  categories,
  selectedProduct,
  onSeePreview,
}) => {
  const form = useFormContext<IAddProductInput>();

  const selectedSubSubCategoryIds = useWatch({
    control: form.control,
    name: "subSubCategoryIds",
  });

  const productVariants = useWatch({
    control: form.control,
    name: "variants",
  });

  const { attributeNameById } = useCategoryVariantAttributes(
    categories,
    selectedSubSubCategoryIds,
    selectedProduct?.variants,
  );

  const variants = productVariants ?? [];

  return (
    <div>
      <div className="flex justify-between align-center text-[#292D32] text-[13px] mb-3 bg-[#FDFAF7] border border-primary rounded-md p-2">
        <span>How my product will look?</span>
        <Button size="xs" type="button" onClick={onSeePreview}>
          See Preview
        </Button>
      </div>

      {variants.length <= VIRTUALIZE_THRESHOLD ? (
        variants.map((variant, index) => (
          <VariantSummaryCard
            key={`${variant.sku}-${index}`}
            variant={variant}
            index={index}
            attributeNameById={attributeNameById}
          />
        ))
      ) : (
        <VirtualizedVariantSummaryRows
          variants={variants}
          attributeNameById={attributeNameById}
        />
      )}
    </div>
  );
};

export default ProductVariantSummary;
